import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtDate } from "../lib/format";
import ExportMenu from "../components/ExportMenu";

const TYPE_COLOR = { "Asset": "#4af6c3", "Liability": "#ff433d", "Equity": "#0068ff", "Income": "#fb8b1e", "Expense": "#ff8c42" };

export default function Ledger() {
    const [coa, setCoa] = useState(null);
    const [ledger, setLedger] = useState(null);
    const [selectedCode, setSelectedCode] = useState(null);
    const [typeFilter, setTypeFilter] = useState("All");
    const loc = useLocation();
    const nav = useNavigate();

    useEffect(() => {
        api.get("/accounting/coa").then((r) => setCoa(r.data));
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(loc.search);
        const code = params.get("code");
        if (code) setSelectedCode(code);
    }, [loc.search]);

    useEffect(() => {
        if (selectedCode) {
            api.get(`/accounting/ledger/${selectedCode}`).then((r) => setLedger(r.data));
        }
    }, [selectedCode]);

    if (!coa) return <div className="font-mono-fin text-sm text-[var(--text-secondary)]">// LOADING CHART OF ACCOUNTS...</div>;

    const filtered = coa.filter((c) => typeFilter === "All" || c.type === typeFilter);

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// GENERAL LEDGER</div>
                <h1 className="font-display font-black text-3xl">Account-wise Ledger Explorer</h1>
                <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                    <Link to="/journals" className="text-[var(--brand-amber)] hover:underline">← Journals</Link> · <Link to="/statements" className="text-[var(--brand-amber)] hover:underline">Statements →</Link>
                </div>
            </div>

            <div className="flex items-center gap-2 font-mono-fin text-[10px] uppercase tracking-wider">
                <span className="text-[var(--text-secondary)]">Filter:</span>
                {["All", "Asset", "Liability", "Equity", "Income", "Expense"].map((t) => (
                    <button
                        key={t}
                        data-testid={`lg-filter-${t.toLowerCase()}`}
                        onClick={() => setTypeFilter(t)}
                        className={`px-3 py-1 border ${typeFilter === t ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)] text-[var(--text-primary)]" : "border-panel text-[var(--text-secondary)]"}`}
                    >
                        {t}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <Panel testId="lg-coa" title="Chart of Accounts" subtitle={`${filtered.length} accounts`} right={<ExportMenu kind="tb" label="Trial Balance" testId="export-tb" size="sm" />}>
                    <div className="max-h-[600px] overflow-y-auto space-y-1">
                        {filtered.map((c) => {
                            const bal = c.debit - c.credit;
                            return (
                                <button
                                    key={c.code}
                                    data-testid={`lg-account-${c.code}`}
                                    onClick={() => { setSelectedCode(c.code); nav(`/ledger?code=${c.code}`, { replace: true }); }}
                                    className={`w-full text-left border p-2 hover:bg-[var(--bg-panel-hover)] transition-colors ${selectedCode === c.code ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)]" : "border-panel"}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[10px] font-mono-fin text-[var(--text-muted)]">{c.code}</div>
                                            <div className="text-xs text-[var(--text-primary)] truncate">{c.name}</div>
                                        </div>
                                        <div className="text-right shrink-0 ml-2">
                                            <span className="text-[9px] uppercase tracking-wider" style={{ color: TYPE_COLOR[c.type] }}>{c.type}</span>
                                            <div className={`text-[11px] font-mono-fin ${bal >= 0 ? "text-[var(--text-primary)]" : "text-neg"}`}>{fmtINR(Math.abs(bal))}</div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </Panel>

                <Panel testId="lg-detail" className="xl:col-span-2" title={ledger ? `${ledger.account_code} · ${ledger.account_name}` : "Select an account →"}>
                    {!ledger ? (
                        <div className="text-center py-16 text-[var(--text-secondary)] font-mono-fin text-xs uppercase tracking-wider">
                            Pick an account from the left to view its ledger with running balance
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-4 gap-3 mb-4">
                                <KpiCard testId="lg-opening" label="Opening" value={ledger.opening} sub="1-Apr-25" />
                                <KpiCard testId="lg-debit" label="Total Debit" value={ledger.total_debit} />
                                <KpiCard testId="lg-credit" label="Total Credit" value={ledger.total_credit} />
                                <KpiCard testId="lg-closing" label="Closing" value={Math.abs(ledger.closing)} sub={ledger.closing >= 0 ? "Dr Balance" : "Cr Balance"} accent />
                            </div>
                            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                                <table className="w-full font-mono-fin text-xs">
                                    <thead className="border-b border-panel sticky top-0 bg-panel">
                                        <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                            <th className="text-left py-2">Date</th>
                                            <th className="text-left">Voucher</th>
                                            <th className="text-left">Narration</th>
                                            <th className="text-right">Debit</th>
                                            <th className="text-right">Credit</th>
                                            <th className="text-right">Balance</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ledger.lines.map((ln, i) => (
                                            <tr key={i} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                                <td className="py-2 text-[var(--text-secondary)]">{fmtDate(ln.date)}</td>
                                                <td className="text-[var(--brand-amber)]">{ln.voucher_no}</td>
                                                <td className="text-[var(--text-primary)] truncate max-w-md">{ln.narration}</td>
                                                <td className="text-right text-[var(--text-primary)]">{ln.debit ? fmtINR(ln.debit) : "—"}</td>
                                                <td className="text-right text-[var(--text-primary)]">{ln.credit ? fmtINR(ln.credit) : "—"}</td>
                                                <td className={`text-right ${ln.running_balance >= 0 ? "text-pos" : "text-neg"}`}>{fmtINR(Math.abs(ln.running_balance))}</td>
                                            </tr>
                                        ))}
                                        {ledger.lines.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[var(--text-secondary)]">No journal entries for this account yet</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </Panel>
            </div>
        </div>
    );
}
